import { z } from 'zod';

import {
  ChessJsRulesPort,
  type AcceptedMove,
  type MoveAttempt,
  type PromotionPiece,
  type RulesColor,
  type RulesPort,
  type RulesSnapshot,
} from '../rules';

const UCI_PATTERN = /^[a-h][1-8][a-h][1-8][qrbn]?$/;

/**
 * Authored puzzle lines are capped at 31 half-moves: sixteen solver turns and
 * fifteen automatic replies. This is deliberately generous for instruction
 * while bounding validation and replay work for external puzzle JSON.
 */
export const MAX_PUZZLE_SOLUTION_PLIES = 31;

const UciMoveSchema = z
  .string()
  .regex(UCI_PATTERN, 'UCI moves must use lowercase coordinate notation, for example e2e4 or e7e8q.');

const FullFenSchema = z
  .string()
  .refine(
    (fen) => fen.trim().split(/\s+/).length === 6,
    'A puzzle start position must use a full six-field FEN.',
  );

const TeachingSchema = z
  .object({
    title: z.string().min(1).max(120).optional(),
    objective: z.string().min(1).max(300).optional(),
    themes: z.array(z.string().min(1).max(80)).max(8).optional(),
    hints: z.array(z.string().min(1).max(240)).min(1).max(3).optional(),
  })
  .strict();

/**
 * Serializable, licensed puzzle data. The semantic refinement guarantees that
 * the whole supplied line is legal when replayed by the canonical RulesPort
 * from startFen.
 */
export const PuzzleDefinitionSchema = z
  .object({
    id: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Puzzle ids must be kebab-case.'),
    startFen: FullFenSchema,
    solutionUci: z
      .array(UciMoveSchema)
      .min(1)
      .max(
        MAX_PUZZLE_SOLUTION_PLIES,
        `A puzzle solution cannot exceed ${MAX_PUZZLE_SOLUTION_PLIES} half-moves.`,
      ),
    provenance: z
      .object({
        source: z.string().min(1).max(200),
        author: z.string().min(1).max(120),
        original: z.boolean(),
      })
      .strict(),
    license: z.string().min(1).max(120),
    teaching: TeachingSchema.optional(),
  })
  .strict()
  .superRefine((puzzle, context) => {
    // Do not perform semantic work on an authored line outside the explicit bound.
    if (puzzle.solutionUci.length > MAX_PUZZLE_SOLUTION_PLIES) {
      return;
    }

    const reconstruction = replayRules(puzzle.startFen, []);
    if (reconstruction === null) {
      context.addIssue({
        code: 'custom',
        path: ['startFen'],
        message: 'The start FEN is not valid.',
      });
      return;
    }

    if (puzzle.solutionUci.length % 2 === 0) {
      context.addIssue({
        code: 'custom',
        path: ['solutionUci'],
        message: 'A solution line must finish with the solver move.',
      });
    }

    if (reconstruction.snapshot.result.terminal) {
      context.addIssue({
        code: 'custom',
        path: ['startFen'],
        message: 'A puzzle cannot start from a terminal chess position.',
      });
      return;
    }

    for (const [index, uci] of puzzle.solutionUci.entries()) {
      const attempted = submitUciMove(reconstruction.rules, uci);
      if (attempted === null || !attempted.accepted) {
        const terminal = attempted?.reason === 'terminal';
        context.addIssue({
          code: 'custom',
          path: ['solutionUci', index],
          message: terminal
            ? `${uci} cannot continue the puzzle because the preceding position is terminal.`
            : `${uci} is not legal from the preceding puzzle position.`,
        });
        return;
      }
    }
  });

export type PuzzleDefinition = z.infer<typeof PuzzleDefinitionSchema>;
export type UciMove = z.infer<typeof UciMoveSchema>;

export type PuzzleStatus = 'active' | 'try-again' | 'solved' | 'revealed';
export type PuzzleResult = 'ready' | 'correct' | 'try-again' | 'solved' | 'revealed';
export type PuzzleMoveRole = 'solver' | 'opponent';

export interface PuzzleMove {
  readonly uci: UciMove;
  readonly san: string;
  readonly from: string;
  readonly to: string;
  readonly promotion?: PromotionPiece;
  readonly role: PuzzleMoveRole;
}

export interface PuzzleHint {
  readonly stage: number;
  readonly totalStages: number;
  readonly text: string;
}

export interface PuzzleState {
  readonly puzzle: PuzzleDefinition;
  readonly solverColor: RulesColor;
  /** Monotonic identity for presentation intents in this puzzle session. */
  readonly revision: number;
  readonly fen: string;
  readonly solutionPly: number;
  readonly moves: readonly PuzzleMove[];
  readonly status: PuzzleStatus;
  readonly result: PuzzleResult;
  readonly hintStage: number;
  readonly hint?: PuzzleHint;
}

export type PuzzleAction =
  | { readonly type: 'attempt-move'; readonly uci: string }
  | { readonly type: 'request-hint' }
  | { readonly type: 'reveal-solution' }
  | { readonly type: 'reset' }
  | { readonly type: 'retry' };

/** Parses external puzzle JSON before it can become domain state. */
export function parsePuzzleDefinition(input: unknown): PuzzleDefinition {
  return PuzzleDefinitionSchema.parse(input);
}

/** Creates a fresh, fully validated puzzle session. */
export function createPuzzleState(input: unknown): PuzzleState {
  const puzzle = parsePuzzleDefinition(input);
  return createPuzzleStateAtRevision(puzzle, 0);
}

function createPuzzleStateAtRevision(
  puzzle: PuzzleDefinition,
  revision: number,
): PuzzleState {
  const reconstruction = replayRules(puzzle.startFen, []);
  if (reconstruction === null) {
    throw new Error('Validated puzzle start position could not be reconstructed.');
  }

  return {
    puzzle,
    solverColor: reconstruction.snapshot.turn,
    revision,
    fen: reconstruction.snapshot.fen,
    solutionPly: 0,
    moves: [],
    status: 'active',
    result: 'ready',
    hintStage: 0,
  };
}

/**
 * Deterministic pure state transition for puzzle interactions. Invalid chess
 * moves, malformed UCI, and inputs after a terminal result preserve identity.
 */
export function reducePuzzle(state: PuzzleState, action: PuzzleAction): PuzzleState {
  switch (action.type) {
    case 'attempt-move':
      return attemptMove(state, action.uci);
    case 'request-hint':
      return requestHint(state);
    case 'reveal-solution':
      return revealSolution(state);
    case 'reset':
    case 'retry':
      return resetPuzzle(state);
  }
}

function attemptMove(state: PuzzleState, attemptedUci: string): PuzzleState {
  if (!isAttemptable(state) || !isUciMove(attemptedUci)) {
    return state;
  }

  const reconstruction = reconstructPuzzleRules(state);
  if (reconstruction === null) {
    return state;
  }

  const attemptedMove = submitUciMove(reconstruction.rules, attemptedUci);
  if (attemptedMove === null || !attemptedMove.accepted) {
    return state;
  }

  const expectedUci = state.puzzle.solutionUci[state.solutionPly];
  const canonicalUci = attemptedMove.value.uci;
  if (canonicalUci !== expectedUci) {
    return {
      ...state,
      revision: state.revision + 1,
      status: 'try-again',
      result: 'try-again',
    };
  }

  const moves: PuzzleMove[] = [
    ...state.moves,
    toPuzzleMove(attemptedMove.value, 'solver'),
  ];
  let solutionPly = state.solutionPly + 1;

  // All opponent entries were semantically verified by PuzzleDefinitionSchema.
  // Submit them through RulesPort nevertheless so terminal policy and every
  // committed reply remain canonical, never copied UCI text.
  while (
    solutionPly < state.puzzle.solutionUci.length
    && reconstruction.rules.snapshot().turn !== state.solverColor
  ) {
    const reply = submitUciMove(
      reconstruction.rules,
      state.puzzle.solutionUci[solutionPly],
    );
    if (reply === null || !reply.accepted) {
      return state;
    }
    moves.push(toPuzzleMove(reply.value, 'opponent'));
    solutionPly += 1;
  }

  const solved = solutionPly === state.puzzle.solutionUci.length;
  const snapshot = reconstruction.rules.snapshot();
  return {
    ...state,
    revision: state.revision + 1,
    fen: snapshot.fen,
    solutionPly,
    moves,
    status: solved ? 'solved' : 'active',
    result: solved ? 'solved' : 'correct',
    hintStage: 0,
    hint: undefined,
  };
}

function requestHint(state: PuzzleState): PuzzleState {
  if (!isAttemptable(state)) {
    return state;
  }

  const expectedUci = state.puzzle.solutionUci[state.solutionPly];
  if (!expectedUci) {
    return state;
  }

  const stages = getHintStages(state.puzzle, expectedUci);
  const hintStage = Math.min(state.hintStage + 1, stages.length);
  if (hintStage === state.hintStage) {
    return state;
  }

  return {
    ...state,
    revision: state.revision + 1,
    hintStage,
    hint: {
      stage: hintStage,
      totalStages: stages.length,
      text: stages[hintStage - 1],
    },
  };
}

function revealSolution(state: PuzzleState): PuzzleState {
  if (!isAttemptable(state)) {
    return state;
  }

  const reconstruction = replayRules(state.puzzle.startFen, []);
  if (reconstruction === null) {
    return state;
  }
  const moves: PuzzleMove[] = [];

  for (const [index, uci] of state.puzzle.solutionUci.entries()) {
    const move = submitUciMove(reconstruction.rules, uci);
    if (move === null || !move.accepted) {
      return state;
    }
    moves.push(toPuzzleMove(move.value, index % 2 === 0 ? 'solver' : 'opponent'));
  }

  const snapshot = reconstruction.rules.snapshot();

  return {
    ...state,
    revision: state.revision + 1,
    fen: snapshot.fen,
    solutionPly: state.puzzle.solutionUci.length,
    moves,
    status: 'revealed',
    result: 'revealed',
    hintStage: 0,
    hint: undefined,
  };
}

function resetPuzzle(state: PuzzleState): PuzzleState {
  return createPuzzleStateAtRevision(state.puzzle, state.revision + 1);
}

function isAttemptable(state: PuzzleState): boolean {
  return state.status === 'active' || state.status === 'try-again';
}

function getHintStages(puzzle: PuzzleDefinition, expectedUci: UciMove): readonly string[] {
  if (puzzle.teaching?.hints) {
    return puzzle.teaching.hints;
  }

  const from = expectedUci.slice(0, 2);
  const to = expectedUci.slice(2, 4);
  const promotion = expectedUci.length === 5 ? ` and promote to ${expectedUci[4]}` : '';
  return [
    `เริ่มดูตัวหมากที่ช่อง ${from}`,
    `ปลายทางคือช่อง ${to}`,
    `ลองเดินจาก ${from} ไป ${to}${promotion}`,
  ];
}

function isUciMove(value: string): value is UciMove {
  return UCI_PATTERN.test(value);
}

interface ReconstructedPuzzleRules {
  readonly rules: RulesPort;
  readonly snapshot: RulesSnapshot;
}

function replayRules(
  initialFen: string,
  acceptedUciEvents: readonly string[],
): ReconstructedPuzzleRules | null {
  const rules: RulesPort = new ChessJsRulesPort();
  const replayed = rules.replay({ initialFen, acceptedUciEvents });
  if (!replayed.accepted) {
    return null;
  }
  return { rules, snapshot: replayed.snapshot };
}

function reconstructPuzzleRules(state: PuzzleState): ReconstructedPuzzleRules | null {
  return replayRules(
    state.puzzle.startFen,
    state.moves.map((move) => move.uci),
  );
}

/**
 * Rebuilds the authoritative puzzle position from its start FEN and accepted
 * move history. The cached current FEN is intentionally not an authority.
 */
export function reconstructPuzzleSnapshot(state: PuzzleState): RulesSnapshot | null {
  return reconstructPuzzleRules(state)?.snapshot ?? null;
}

function submitUciMove(
  rules: RulesPort,
  uci: string,
): MoveAttempt<AcceptedMove> | null {
  if (!isUciMove(uci)) {
    return null;
  }

  const promotion = uci.length === 5 ? toPromotionPiece(uci[4]) : undefined;
  if (uci.length === 5 && promotion === undefined) {
    return null;
  }

  const snapshot = rules.snapshot();
  return rules.submit({
    expectedRevision: snapshot.revision,
    from: uci.slice(0, 2),
    to: uci.slice(2, 4),
    ...(promotion === undefined ? {} : { promotion }),
  });
}

function toPromotionPiece(value: string): PromotionPiece | undefined {
  if (value === 'q' || value === 'r' || value === 'b' || value === 'n') {
    return value;
  }
  return undefined;
}

function toPuzzleMove(move: AcceptedMove, role: PuzzleMoveRole): PuzzleMove {
  return {
    uci: move.uci,
    san: move.san,
    from: move.verbose.from,
    to: move.verbose.to,
    ...(move.verbose.promotion ? { promotion: move.verbose.promotion } : {}),
    role,
  };
}
